// Detect whether a connected gamepad is an Xbox or PlayStation controller
// so the bindings page can render the right face-button glyphs. Reads
// navigator.getGamepads(); when no controller is present, falls back to
// Xbox per the user spec.
//
// Chrome/Firefox bake the USB vendor id into Gamepad.id ("054c-..." for
// Sony, "045e-..." for Microsoft). Some browsers report a free-form name
// only (e.g. "Wireless Controller") — the heuristic checks vendor ids
// first, then falls through to substring match.

export type ControllerVendor = 'xbox' | 'playstation';

export function detectControllerVendor(): ControllerVendor {
  const pads = navigator.getGamepads?.() ?? [];
  for (const p of pads) {
    if (!p || !p.connected) continue;
    const id = p.id.toLowerCase();
    // Sony vendor id 054c covers DualShock 3/4 + DualSense across
    // Chromium browsers. Substring matches handle Firefox / Safari
    // variants that report the brand name instead.
    if (id.includes('054c')
      || id.includes('dualshock')
      || id.includes('dualsense')
      || id.includes('playstation')) {
      return 'playstation';
    }
    // Microsoft vendor id 045e + the generic xinput mapping that every
    // Xbox / Xbox-clone pad reports under StandardMapping.
    if (id.includes('045e')
      || id.includes('xbox')
      || id.includes('xinput')) {
      return 'xbox';
    }
  }
  return 'xbox';
}

/** Face-button glyphs for the active vendor. Used by the bindings tab to
 *  render Jump / Sprint / JumpKick labels in the column. The Unicode
 *  glyphs are: ✕ U+2715, ◯ U+25EF, □ U+25A1, △ U+25B3. */
export interface FaceLabels {
  bottom: string; // A or ✕
  right: string;  // B or ◯
  left: string;   // X or □
  top: string;    // Y or △
}

export const XBOX_FACE: FaceLabels = {
  bottom: 'A', right: 'B', left: 'X', top: 'Y'
};

export const PS_FACE: FaceLabels = {
  bottom: '✕', right: '◯', left: '□', top: '△'
};

export function faceLabelsFor(vendor: ControllerVendor): FaceLabels {
  return vendor === 'playstation' ? PS_FACE : XBOX_FACE;
}
